import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Plus, Pencil, FileBarChart, Trash2, AlertTriangle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { RagBadge } from "@/components/Rag";
import { Select } from "@/pages/Projects";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const BLANK = {
  name: "", code: "", account_manager: "", contact_name: "", contact_email: "",
  contact_phone: "", sector: "", relationship: "Active", contract_reference: "",
  contract_value: "", notes: "",
};

const RELATIONSHIPS = ["Prospect", "Active", "On Hold", "Closed"];

const money = (v) =>
  typeof v === "number" && v
    ? `£${v.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`
    : "—";

export default function Clients() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(
    () => api.get("/clients/summary").then(({ data }) => setData(data)),
    [],
  );
  useEffect(() => { load(); }, [load]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const startNew = () => { setEditing(null); setForm(BLANK); setOpen(true); };
  const startEdit = (client) => {
    setEditing(client);
    setForm({ ...BLANK, ...client, contract_value: client.contract_value ?? "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Give the client a name");
    setBusy(true);
    try {
      const payload = {
        ...form,
        contract_value: form.contract_value === "" ? null : Number(form.contract_value),
      };
      if (editing) await api.put(`/clients/${editing.id}`, payload);
      else await api.post("/clients", payload);
      setOpen(false);
      await load();
      toast.success(editing ? "Client updated" : "Client added");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save the client");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (client) => {
    if (!window.confirm(
      `Remove ${client.name}? Their ${client.projects} project${client.projects === 1 ? "" : "s"} will stay, simply unassigned.`,
    )) return;
    try {
      await api.delete(`/clients/${client.id}`);
      await load();
      toast.success("Client removed. Projects were kept and unassigned.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const t = data?.totals;

  return (
    <Shell
      testId="clients-page"
      title="Clients"
      subtitle="Group projects by the customer they belong to, then report per client"
      actions={
        <Button data-testid="client-new-btn" onClick={startNew} className="gap-1.5"
          style={{ backgroundColor: "var(--brand-primary)" }}>
          <Plus size={15} /> Add client
        </Button>
      }
    >
      {t && (
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-sm bg-slate-200 lg:grid-cols-5">
          {[
            ["clients-kpi-clients", "Clients", t.clients],
            ["clients-kpi-projects", "Projects", t.projects],
            ["clients-kpi-contract", "Contract value", money(t.contract_value)],
            ["clients-kpi-allocated", "Allocated budget", money(t.allocated_budget)],
            ["clients-kpi-attention", "Needing attention", `${t.red} red · ${t.amber} amber`],
          ].map(([testId, label, value]) => (
            <div key={testId} data-testid={testId} className="bg-white px-5 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</p>
              <p className="mt-1.5 truncate text-xl font-bold text-slate-900">{value}</p>
            </div>
          ))}
        </div>
      )}

      {data?.unassigned_projects > 0 && (
        <p data-testid="clients-unassigned-note"
          className="mt-4 flex items-center gap-2 rounded-sm bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
          <AlertTriangle size={14} />
          {data.unassigned_projects} project{data.unassigned_projects === 1 ? " is" : "s are"} not
          assigned to a client, so they will not appear in any client pack.
        </p>
      )}

      <div className="panel mt-6 overflow-x-auto">
        {!data?.clients?.length ? (
          <Empty
            icon={Building2}
            title="No clients yet"
            body="Add a client, assign projects to it, and you can report per client with a full pack."
          />
        ) : (
          <table data-testid="clients-table" className="w-full min-w-[920px] text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-slate-400">
                <th className="px-5 py-2.5 font-semibold">Client</th>
                <th className="px-3 py-2.5 font-semibold">Account manager</th>
                <th className="px-3 py-2.5 font-semibold">Projects</th>
                <th className="px-3 py-2.5 font-semibold">RAG</th>
                <th className="px-3 py-2.5 font-semibold">Complete</th>
                <th className="px-3 py-2.5 font-semibold">Risks</th>
                <th className="px-3 py-2.5 font-semibold">Issues</th>
                <th className="px-3 py-2.5 font-semibold">Overdue</th>
                <th className="px-3 py-2.5 font-semibold">Allocated</th>
                <th className="px-3 py-2.5 font-semibold text-right">Report</th>
              </tr>
            </thead>
            <tbody>
              {data.clients.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`}
                  className="border-t border-slate-100 text-slate-700">
                  <td className="px-5 py-3">
                    <button data-testid={`client-open-${c.id}`}
                      onClick={() => navigate(`/clients/${c.id}/report`)}
                      className="font-semibold text-slate-900 hover:underline">
                      {c.name}
                    </button>
                    <p className="text-xs text-slate-500">
                      {c.code || "—"} · {c.relationship}
                    </p>
                  </td>
                  <td className="px-3 py-3">{c.account_manager || "—"}</td>
                  <td className="px-3 py-3 tabular-nums">{c.projects}</td>
                  <td className="px-3 py-3"><RagBadge value={c.rag} /></td>
                  <td className="px-3 py-3 tabular-nums">{c.avg_percent_complete}%</td>
                  <td className="px-3 py-3 tabular-nums">{c.open_risks}</td>
                  <td className="px-3 py-3 tabular-nums">{c.open_issues}</td>
                  <td className="px-3 py-3 tabular-nums">
                    {c.overdue_milestones > 0
                      ? <span className="font-semibold text-rose-700">{c.overdue_milestones}</span>
                      : "0"}
                  </td>
                  <td className="px-3 py-3 tabular-nums">{money(c.allocated_budget)}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" data-testid={`client-report-${c.id}`}
                        onClick={() => navigate(`/clients/${c.id}/report`)} className="gap-1.5">
                        <FileBarChart size={14} /> Report
                      </Button>
                      <Button variant="ghost" size="sm" data-testid={`client-edit-${c.id}`}
                        onClick={() => startEdit(c)}>
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" data-testid={`client-delete-${c.id}`}
                        onClick={() => remove(c)} className="text-rose-600 hover:bg-rose-50">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="client-dialog" className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit client" : "Add a client"}</DialogTitle>
            <DialogDescription>
              A client groups projects by customer. It sits alongside your portfolios rather than
              inside them, so a client's work can span as many portfolios as it needs to.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="field-label">Client name</label>
              <Input data-testid="client-name-input" value={form.name}
                onChange={(e) => set("name")(e.target.value)} placeholder="Meridian Borough Council" />
            </div>
            <div>
              <label className="field-label">Client code</label>
              <Input data-testid="client-code-input" value={form.code || ""}
                onChange={(e) => set("code")(e.target.value)} placeholder="MBC" />
            </div>
            <div>
              <label className="field-label">Relationship</label>
              <Select testId="client-relationship-select" value={form.relationship}
                onChange={set("relationship")}
                options={RELATIONSHIPS.map((r) => ({ value: r, label: r }))} />
            </div>
            <div>
              <label className="field-label">Account manager</label>
              <Input data-testid="client-am-input" value={form.account_manager || ""}
                onChange={(e) => set("account_manager")(e.target.value)} />
            </div>
            <div>
              <label className="field-label">Sector</label>
              <Input data-testid="client-sector-input" value={form.sector || ""}
                onChange={(e) => set("sector")(e.target.value)} />
            </div>
            <div>
              <label className="field-label">Contact name</label>
              <Input data-testid="client-contact-input" value={form.contact_name || ""}
                onChange={(e) => set("contact_name")(e.target.value)} />
            </div>
            <div>
              <label className="field-label">Contact email</label>
              <Input data-testid="client-email-input" value={form.contact_email || ""}
                onChange={(e) => set("contact_email")(e.target.value)} />
            </div>
            <div>
              <label className="field-label">Contract reference</label>
              <Input data-testid="client-contract-input" value={form.contract_reference || ""}
                onChange={(e) => set("contract_reference")(e.target.value)} />
            </div>
            <div>
              <label className="field-label">Contract value (£)</label>
              <Input data-testid="client-value-input" type="number" value={form.contract_value}
                onChange={(e) => set("contract_value")(e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Notes</label>
              <Textarea data-testid="client-notes-input" rows={3} value={form.notes || ""}
                onChange={(e) => set("notes")(e.target.value)} />
            </div>
          </div>
          <Button data-testid="client-save-btn" disabled={busy} onClick={save}
            className="mt-4 w-full" style={{ backgroundColor: "var(--brand-primary)" }}>
            {editing ? "Save client" : "Add client"}
          </Button>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
